import { STATION_LOGO, imageSrc } from '@/lib/images'
import { lumi } from '@/lib/lumi'
import { makeExcerpt } from '@/lib/news'
import type { NewsArticle } from '@/types/news'

const WIDTH = 1080
const HEIGHT = 1350
const IMAGE_HEIGHT = 760
const PAD = 56

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxLines: number) {
  const words = (text || '').replace(/\s+/g, ' ').trim().split(' ').filter(Boolean)
  const lines: string[] = []
  let current = ''

  for (const word of words) {
    const test = current ? `${current} ${word}` : word
    if (ctx.measureText(test).width <= maxWidth) {
      current = test
      continue
    }
    if (current) lines.push(current)
    current = word
    if (lines.length === maxLines - 1) break
  }

  if (lines.length < maxLines && current) {
    let line = current
    if (ctx.measureText(line).width > maxWidth) {
      while (line.length > 1 && ctx.measureText(`${line}...`).width > maxWidth) {
        line = line.slice(0, -1)
      }
      line = `${line.trim()}...`
    }
    lines.push(line)
  } else if (lines.length === maxLines && words.join(' ').length > lines.join(' ').length) {
    let last = lines[maxLines - 1] || ''
    while (last.length > 1 && ctx.measureText(`${last}...`).width > maxWidth) {
      last = last.slice(0, -1)
    }
    lines[maxLines - 1] = `${last.trim()}...`
  }

  return lines
}

function formatPostDate(iso?: string) {
  if (!iso) return ''
  try {
    return new Date(iso)
      .toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
      .replace('.', '')
      .toUpperCase()
  } catch {
    return ''
  }
}

function waitFonts() {
  if (typeof document === 'undefined' || !document.fonts?.ready) return Promise.resolve()
  return Promise.race([
    Promise.resolve(document.fonts.ready).then(() => undefined, () => undefined),
    new Promise<void>((resolve) => {
      window.setTimeout(resolve, 800)
    }),
  ])
}

function isInlineSrc(src: string) {
  return src.startsWith('data:') || src.startsWith('blob:')
}

function imageFromSrc(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Não foi possível carregar a imagem da matéria.'))
    img.src = src
  })
}

function blobToDataUrl(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = String(reader.result || '')
      if (!result.startsWith('data:')) {
        reject(new Error('Não foi possível ler a imagem da matéria.'))
        return
      }
      resolve(result)
    }
    reader.onerror = () => reject(new Error('Não foi possível ler a imagem da matéria.'))
    reader.readAsDataURL(blob)
  })
}

async function blobFromUrl(src: string) {
  const response = await fetch(src, { mode: 'cors', credentials: 'omit', cache: 'no-store' })
  if (!response.ok) throw new Error('Não foi possível obter a imagem da matéria.')
  const blob = await response.blob()
  if (!blob || blob.size < 8) throw new Error('Imagem da matéria inválida.')
  return blob
}

function invokeError(error: unknown) {
  const err = error as { data?: { error?: string }; message?: string }
  return err?.data?.error || err?.message || 'Não foi possível preparar a imagem da matéria.'
}

async function fetchViaProxy(src: string) {
  try {
    const result = await lumi.functions.invoke('imageProxy', {
      method: 'POST',
      body: { url: src },
    }) as { dataUrl?: string; error?: string }
    const dataUrl = result?.dataUrl
    if (dataUrl?.startsWith('data:')) return dataUrl
    throw new Error(result?.error || 'Não foi possível preparar a imagem da matéria.')
  } catch (error) {
    throw new Error(invokeError(error))
  }
}

async function loadDrawableImage(src: string, label: string) {
  const value = (src || '').trim()
  if (!value) throw new Error(`${label} em falta.`)

  if (isInlineSrc(value)) {
    return imageFromSrc(value)
  }

  try {
    const blob = await blobFromUrl(value)
    const dataUrl = await blobToDataUrl(blob)
    return imageFromSrc(dataUrl)
  } catch {
    const dataUrl = await fetchViaProxy(value)
    return imageFromSrc(dataUrl)
  }
}

function drawCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number,
) {
  const iw = img.naturalWidth || img.width
  const ih = img.naturalHeight || img.height
  if (!iw || !ih) return
  const scale = Math.max(w / iw, h / ih)
  const dw = iw * scale
  const dh = ih * scale
  ctx.save()
  ctx.beginPath()
  ctx.rect(x, y, w, h)
  ctx.clip()
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh)
  ctx.restore()
}

function canvasToJpeg(canvas: HTMLCanvasElement) {
  try {
    return canvas.toDataURL('image/jpeg', 0.92)
  } catch {
    throw new Error('A imagem da matéria bloqueou a geração. Tente outra foto.')
  }
}

export async function generateInstagramPost(article: NewsArticle) {
  await waitFonts()

  const canvas = document.createElement('canvas')
  canvas.width = WIDTH
  canvas.height = HEIGHT
  const ctx = canvas.getContext('2d', { alpha: false })
  if (!ctx) throw new Error('O navegador não consegue gerar a imagem.')

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, WIDTH, HEIGHT)

  const photo = await loadDrawableImage(imageSrc(article.featuredImage), 'A imagem da matéria')
  drawCover(ctx, photo, 0, 0, WIDTH, IMAGE_HEIGHT)

  try {
    const logo = await loadDrawableImage(STATION_LOGO, 'O logótipo')
    const logoH = 92
    const ratio = (logo.naturalWidth || logo.width) / (logo.naturalHeight || logo.height || 1)
    const logoW = Math.max(1, ratio * logoH)
    ctx.drawImage(logo, WIDTH - logoW - 36, IMAGE_HEIGHT - logoH - 28, logoW, logoH)
  } catch {
    // watermark is optional if the logo fails to load
  }

  let y = IMAGE_HEIGHT + 48
  const maxWidth = WIDTH - PAD * 2

  ctx.fillStyle = '#2a63e8'
  ctx.font = '800 28px Poppins, Nunito, sans-serif'
  ctx.fillText((article.category || 'Geral').toUpperCase(), PAD, y)

  y += 64
  ctx.fillStyle = '#12307a'
  ctx.font = '900 58px Nunito, Poppins, sans-serif'
  const titleLines = wrapText(ctx, article.title || '', maxWidth, 3)
  titleLines.forEach((line) => {
    ctx.fillText(line, PAD, y)
    y += 68
  })

  y += 8
  ctx.fillStyle = '#5b6b86'
  ctx.font = '500 32px Poppins, sans-serif'
  const excerpt = makeExcerpt(article.excerpt || article.body || '', 160)
  wrapText(ctx, excerpt, maxWidth, 3).forEach((line) => {
    ctx.fillText(line, PAD, y)
    y += 44
  })

  y += 28
  ctx.fillStyle = '#2a63e8'
  ctx.font = '800 26px Poppins, sans-serif'
  ctx.fillText(`POR ${(article.author || 'Redação').toUpperCase()}`, PAD, y)

  y += 48
  ctx.fillStyle = '#94a3b8'
  ctx.font = '700 24px Poppins, sans-serif'
  ctx.fillText(formatPostDate(article.createdAt), PAD, y)

  return canvasToJpeg(canvas)
}

export function downloadDataUrl(dataUrl: string, filename: string) {
  const link = document.createElement('a')
  link.href = dataUrl
  link.download = filename || 'instagram-bonitafm.jpg'
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  link.remove()
}
